import { useCallback, useRef, useState } from 'react'
import { Device, Call } from '@twilio/voice-sdk'
import { useBargeInMutation } from '../../api/monitoring'

export type BargeInState = 'idle' | 'connecting' | 'connected' | 'error'

interface UseBargeInReturn {
  bargeIn: (callId: string) => void
  hangUp: () => void
  state: BargeInState
  isConnected: boolean
  isConnecting: boolean
  isMuted: boolean
  toggleMute: () => void
  error: string | null
}

export function useBargeIn(): UseBargeInReturn {
  const [state, setState] = useState<BargeInState>('idle')
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const bargeInMutation = useBargeInMutation()

  const cleanup = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    if (deviceRef.current) {
      deviceRef.current.destroy()
      deviceRef.current = null
    }
    setIsMuted(false)
  }, [])

  const bargeIn = useCallback(
    async (callId: string) => {
      cleanup()
      setState('connecting')
      setError(null)

      try {
        const data = await bargeInMutation.mutateAsync(callId)

        const device = new Device(data.token, {
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          closeProtection: true,
        })
        deviceRef.current = device

        await device.register()

        const call = await device.connect({
          params: { conference_name: data.conference_name },
        })
        callRef.current = call

        call.on('accept', () => {
          setState('connected')
        })

        call.on('disconnect', () => {
          setState('idle')
          cleanup()
        })

        call.on('error', (err: { message?: string }) => {
          setError(err.message || 'Call error')
          setState('error')
          cleanup()
        })

        call.on('cancel', () => {
          setState('idle')
          cleanup()
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to barge in'
        setError(message)
        setState('error')
        cleanup()
      }
    },
    [bargeInMutation, cleanup],
  )

  const hangUp = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
    }
    setState('idle')
    cleanup()
  }, [cleanup])

  const toggleMute = useCallback(() => {
    if (callRef.current) {
      const newMuted = !callRef.current.isMuted()
      callRef.current.mute(newMuted)
      setIsMuted(newMuted)
    }
  }, [])

  return {
    bargeIn,
    hangUp,
    state,
    isConnected: state === 'connected',
    isConnecting: state === 'connecting',
    isMuted,
    toggleMute,
    error,
  }
}
